import { Component } from "@angular/core";
import { GridReadyEvent } from "@ag-grid-community/core";
import { DialogBaseComponent, DialogParams } from "app/shared/dialog-base.component";
import { DomainService } from "app/shared/domain.service";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { CoreChoicePreviewItem } from "app/shared/db-save.service";
import { AgFns, GridOptionsExt, RowSelectionMultipleCheckbox } from "app/shared/ag-fns";
import _ from "lodash";

export interface ApplyCoreChoicesPreviewParams extends DialogParams {
  previewItems: CoreChoicePreviewItem[];
}

@Component({
  templateUrl: "./apply-core-choices-preview-dialog.component.html",
  standalone: false,
})
export class ApplyCoreChoicesPreviewDialogComponent extends DialogBaseComponent<ApplyCoreChoicesPreviewParams> {
  gridOptions!: GridOptionsExt;
  previewItems: CoreChoicePreviewItem[] = [];
  selectedRowCount = 0;
  selectedProductCount = 0;
  get totalProducts(): number {
    return _.sumBy(this.previewItems, (x) => x.productCount);
  }

  constructor(
    public config: DynamicDialogConfig<any>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService
  ) {
    super(config, dialogRef, domainService);
    this.prepare();
  }

  private async prepare() {
    this.previewItems = this.params.previewItems;

    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady,
      onSelectionChanged: this.onSelectionChanged.bind(this),
      getRowId: (params) => params.data.productTypeId.toString(),
      rowSelection: {
        mode: 'multiRow',
        enableClickSelection: true,
        checkboxes: true,
        headerCheckbox: true,
      },
    });

    this.config.header = `Apply Core Choices — Preview (${this.previewItems.length} product types, ${this.totalProducts.toLocaleString()} products to create)`;
    this.isPageReady = true;
  }

  onSelectionChanged() {
    const selected = this.gridOptions.apiX?.getSelectedRows() as CoreChoicePreviewItem[] ?? [];
    this.selectedRowCount = selected.length;
    this.selectedProductCount = _.sumBy(selected, x => x.productCount);
  }

  async onGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        headerName: "Manufacturer",
        field: "manufacturerName",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(120),
      },
      {
        headerName: "SKU",
        field: "sku",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(100),
      },
      {
        headerName: "Description",
        field: "description",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(150),
      },
      {
        headerName: "Missing Choices",
        field: "missingChoices",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(200),
      },
      {
        headerName: "Products to Create",
        field: "productCount",
        filter: "agNumberColumnFilter",
        type: "numericColumn",
        ...AgFns.fixWidth(150),
      },
    ];

    const sortModel = [
      { colId: "manufacturerName", sort: "asc" as const },
      { colId: "sku", sort: "asc" as const },
    ];

    AgFns.initGrid(event, colDefs, sortModel);
  }

  onApply() {
    const selectedRows = this.gridOptions.apiX?.getSelectedRows() as CoreChoicePreviewItem[];
    if (!selectedRows || selectedRows.length === 0) return;
    const selectedPtIds = selectedRows.map((r) => r.productTypeId);
    this.dialogRef.close(selectedPtIds);
  }

  onCancel() {
    this.dialogRef.close(null);
  }
}
